import React from "react"
import ThemeProvider from "@material-ui/styles/ThemeProvider"

import Layout from "../../../components/layout"
import SEO from "../../../components/seo"
import PracticeRoutinePost from "../../../components/practice-routine-post"
import theme from "../../../theme"

const summary = [
  {
    level: "Preliminary to Grade 2",
    time: "15 to 25 minutes",
    focus: "Posture, right-hand pattern, left-hand placement, simple melodies and chords",
    weeklyHabit: "Practise slowly with clean starts, clean stops, and relaxed hands",
  },
  {
    level: "Grades 3 to 4",
    time: "30 to 40 minutes",
    focus: "Scales, arpeggios, position shifts, tone, rhythm accuracy",
    weeklyHabit: "Alternate technical drills with small sections of repertoire",
  },
  {
    level: "Grades 5 to 6",
    time: "45 to 60 minutes",
    focus: "Barre control, articulation, dynamics, fluency, fretboard awareness",
    weeklyHabit: "Use a metronome and isolate shifts, chord changes, and awkward fingerings",
  },
  {
    level: "Grades 7 to 8",
    time: "60 to 90 minutes",
    focus: "Tone colour, style, stamina, advanced coordination, performance confidence",
    weeklyHabit: "Plan full-program runs alongside slow technical repair work",
  },
]

const sections = [
  {
    title: "Preliminary to Grade 2: keep both hands relaxed",
    paragraphs: [
      "Early guitar practice should be short and calm. Begin with sitting position, hand shape, open strings, simple right-hand patterns, and left-hand finger placement close to the frets.",
      "Use tiny piece sections and stop before tension builds. Clean sound is more important than speed at this stage.",
    ],
  },
  {
    title: "Grades 3 to 4: connect technique to pieces",
    paragraphs: [
      "Students can start practising for 30 to 40 minutes with regular scale, arpeggio, rhythm, and sight-reading work. Keep the sound even and check that both hands stay coordinated.",
      "When a piece is difficult, identify whether the problem is rhythm, a shift, a chord change, or right-hand pattern. Fix that one thing before playing the whole piece again.",
    ],
  },
  {
    title: "Grades 5 to 6: build reliability",
    paragraphs: [
      "At middle grades, practice should include metronome work, slow shifts, controlled barre practice, and tone work across different strings and positions.",
      "Record short sections to check rhythmic steadiness and clarity. Guitar students often improve quickly when they can hear whether notes are ringing cleanly or being cut off too soon.",
    ],
  },
  {
    title: "Grades 7 to 8: shape the performance",
    paragraphs: [
      "Advanced guitar practice needs stamina, style, and colour. Rotate priorities through the week: technical work, tone production, interpretation, sight reading, and performance runs.",
      "Mock performances are especially useful because they reveal whether shifts, memory, and tone stay secure when the student plays without stopping.",
    ],
  },
  {
    title: "A simple weekly rhythm",
    paragraphs: [
      "A shared calendar can make guitar practice more specific: technique, rhythm, repertoire detail, sight reading, recording, and rest can each have a place.",
      "The best routine prevents random repetition and gives the student one clear musical target for each session.",
    ],
  },
]

const GuitarPracticeRoutinesAmebLevels = () => (
  <ThemeProvider theme={theme}>
    <Layout>
      <SEO
        title="Guitar Practice Schedule by AMEB Level"
        description="Plan guitar practice for AMEB levels with focused routines for technique, repertoire, sight reading, rhythm, tone, and performance readiness."
      />
      <PracticeRoutinePost
        instrument="Guitar"
        title="Guitar practice routines for different AMEB levels"
        intro="A strong guitar routine keeps both hands coordinated, protects relaxed technique, and links every exercise back to the sound the student wants in their pieces."
        sharedCalendarUrl="https://app.schoolcalendar.me/calendar/selfish-jellyfish-15"
        image="blog-guitar.png"
        imageAlt="Guitar practice planning"
        summary={summary}
        sections={sections}
      />
    </Layout>
  </ThemeProvider>
)

export default GuitarPracticeRoutinesAmebLevels
